import { 
  NotificationConfig, 
  InboxResponse, 
  FetchInboxOptions, 
  MarkReadOptions,
  ConnectRealtimeOptions,
  WebSocketMessage 
} from './types.js';

class AlphaLearnNotificationsSDK {
  private config: NotificationConfig;
  private wsConnection: WebSocket | null = null;
  private wsReconnectAttempts = 0;
  private wsMaxReconnectAttempts = 5;
  private wsReconnectDelay = 1000;

  constructor() {
    // Get configuration from global window object or data attributes
    this.config = this.getConfig();
  }

  private getConfig(): NotificationConfig {
    // Try global configuration first
    const globalConfig = (window as unknown as Record<string, unknown>).ALPHALEARN_NOTIFS as Record<string, unknown> | undefined;
    if (globalConfig) {
      return {
        apiBase: globalConfig.apiBase as string,
        wsBase: globalConfig.wsBase as string,
      };
    }

    // Fallback to defaults (should be overridden in production)
    return {
      apiBase: 'https://api.notifications.example.com',
      wsBase: 'wss://ws.notifications.example.com',
    };
  }

  async fetchInbox(options: FetchInboxOptions): Promise<InboxResponse> {
    const { onerosterId, limit = 20, cursor, unreadOnly, authToken } = options;
    
    const params = new URLSearchParams({
      oneroster_id: onerosterId,
      limit: limit.toString(),
    });

    if (cursor) params.set('cursor', cursor);
    if (unreadOnly) params.set('unread_only', 'true');

    const url = `${this.config.apiBase}/inbox?${params.toString()}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch inbox:', error);
      throw error;
    }
  }

  async markRead(options: MarkReadOptions): Promise<void> {
    const { onerosterId, ids, authToken } = options;
    
    const url = `${this.config.apiBase}/read`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          oneroster_id: onerosterId,
          notification_ids: ids,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      await response.json();
    } catch (error) {
      console.error('Failed to mark read:', error);
      throw error;
    }
  }

  connectRealtime(options: ConnectRealtimeOptions): () => void {
    const { authToken, onMessage, onError, onConnect, onDisconnect } = options;

    const connect = () => {
      // Construct WebSocket URL with query parameters (no path)
      const wsUrl = `${this.config.wsBase}?token=${encodeURIComponent(authToken)}`;


      try {
        this.wsConnection = new WebSocket(wsUrl);

        this.wsConnection.onopen = () => {
          this.wsReconnectAttempts = 0;
          onConnect?.();
        };

        this.wsConnection.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            onMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error, 'Raw data:', event.data);
          }
        };

        this.wsConnection.onclose = (event) => {
          this.wsConnection = null;
          onDisconnect?.();

          // Attempt reconnection if not manually closed
          if (event.code !== 1000 && this.wsReconnectAttempts < this.wsMaxReconnectAttempts) {
            this.wsReconnectAttempts++;
            const delay = this.wsReconnectDelay * Math.pow(2, this.wsReconnectAttempts - 1);

            setTimeout(() => {
              connect();
            }, delay);
          } else if (this.wsReconnectAttempts >= this.wsMaxReconnectAttempts) {
            // Max attempts reached, stop reconnecting
          }
        };

        this.wsConnection.onerror = (event: Event) => {
          // Extract meaningful error information
          let errorMessage = 'Unknown WebSocket error';
          errorMessage = `WebSocket connection failed (Event type: ${event.type})`;
          if ('message' in event && typeof (event as unknown as {message?: string}).message === 'string') {
            errorMessage += ` - ${(event as unknown as {message: string}).message}`;
          }

          console.error('❌ WebSocket error:', errorMessage, event);
          onError?.(new Error(errorMessage));
        };

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create WebSocket connection';
        console.error('❌ Failed to create WebSocket connection:', errorMessage);
        onError?.(new Error(errorMessage));
      }
    };

    connect();

    // Return unsubscribe function
    return () => {
      if (this.wsConnection) {
        this.wsConnection.close(1000, 'Manual disconnect');
        this.wsConnection = null;
      }
    };
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.config.apiBase}/healthz`, {
        method: 'GET',
      });
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const notificationsSDK = new AlphaLearnNotificationsSDK();

// Export individual functions for convenience
export async function fetchInbox(options: FetchInboxOptions): Promise<InboxResponse> {
  return notificationsSDK.fetchInbox(options);
}

export async function markRead(options: MarkReadOptions): Promise<void> {
  return notificationsSDK.markRead(options);
}

export function connectRealtime(options: ConnectRealtimeOptions): () => void {
  return notificationsSDK.connectRealtime(options);
}